// src/pages/DCSentry.jsx
// Route: /dc-sentry
// Register in App.jsx: <Route path="/dc-sentry" element={<DCSentry />} />
//
// v2.0 — Major rebuild:
//   • Live data polling (30s) replacing mock-only static topology
//   • DC Site Selector: groups agents by site (DC_OT_AUSTIN_01 → AUSTIN)
//     showing paired OT + OOB agent health side-by-side
//   • Split OT/OOB topology: two distinct zone planes with dedicated agent nodes
//   • Cross-network correlation banner when CORR rules fire
//   • 4-tab AI Panel: Correlations (priority), OOB Threats, OT Threats, Events
//   • Protocol-aware event rows with DC-specific context
//   • Mock assets as fallback only when no live DC assets discovered

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';
import { sendChatQuery } from '../services/chat';
import api from '../services/api';

// ════════════════════════════════════════════════════════════════════════════
// MARKDOWN STYLING COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const SiteIntelBanner = ({ siteName, data, loading }) => {
  const qd = data?.['queue_delay'];
  const sr = data?.['site_risk'];
  const pr = data?.['procurement_risk'];

  const scoreChip = (label, row) => {
    if (!row) return (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-400 font-medium uppercase">{label}</span>
        <span className="text-[10px] text-slate-400">{'\u2014'}</span>
      </div>
    );
    const s = Math.round(row.score);
    const color = s >= 75 ? 'text-red-600 bg-red-50 border-red-200'
                : s >= 50 ? 'text-amber-600 bg-amber-50 border-amber-200'
                : s >= 25 ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
                :           'text-emerald-600 bg-emerald-50 border-emerald-200';
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-500 font-medium uppercase">{label}</span>
        <span className={`px-1.5 py-0.5 rounded-md border text-[10px] font-bold ${color}`}>
          {s}/100
        </span>
        <span className="text-[9px] text-slate-400">{row.confidence}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="absolute top-[188px] left-0 right-0 z-30 mx-6">
        <div className="bg-slate-50/90 backdrop-blur-sm border border-slate-200
                        rounded-xl px-4 py-2 flex items-center gap-3 animate-pulse">
          <span className="text-[10px] text-slate-400">Loading CORR-004 intelligence...</span>
        </div>
      </div>
    );
  }

  if (!data && !loading) return null;

  const highQueueRisk = qd && qd.score >= 75;
  const modQueueRisk  = qd && qd.score >= 50 && qd.score < 75;

  return (
    <div className="absolute top-[188px] left-0 right-0 z-30 mx-6">
      <div className={`backdrop-blur-sm border rounded-xl px-4 py-2 flex items-center gap-4 flex-wrap
        ${highQueueRisk
          ? 'bg-red-50/95 border-red-200'
          : modQueueRisk
          ? 'bg-amber-50/95 border-amber-200'
          : 'bg-white/90 border-slate-200'}`}>

        {/* Label */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">
            CORR-004 {siteName}
          </span>
        </div>

        {/* Divider */}
        <div className="w-px h-4 bg-slate-200 shrink-0" />

        {/* Scores */}
        <div className="flex items-center gap-4 flex-wrap">
          {scoreChip('Queue Delay', qd)}
          {scoreChip('Site Risk', sr)}
          {scoreChip('Procurement', pr)}
        </div>

        {/* Callout */}
        {highQueueRisk && (
          <>
            <div className="w-px h-4 bg-red-200 shrink-0" />
            <span className="text-[10px] text-red-600 font-semibold">
              High interconnection slip risk — engage ISO early
            </span>
          </>
        )}
        {modQueueRisk && (
          <>
            <div className="w-px h-4 bg-amber-200 shrink-0" />
            <span className="text-[10px] text-amber-600 font-semibold">
              Moderate queue delay risk — build 6-month timeline buffer
            </span>
          </>
        )}

        {/* Data freshness */}
        {qd?.computed_at && (
          <div className="ml-auto text-[9px] text-slate-400 shrink-0">
            Updated {new Date(qd.computed_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

const MarkdownComponents = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-slate-800 mb-3 mt-4 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-cyan-700 mb-2 mt-4 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-cyan-600 mb-2 mt-3">{children}</h3>,
  p: ({ children }) => <p className="text-slate-700 text-sm mb-2 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="text-slate-900 font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc list-inside text-slate-700 text-sm mb-2 space-y-1 ml-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-slate-700 text-sm mb-2 space-y-1 ml-2">{children}</ol>,
  li: ({ children }) => <li className="text-slate-700">{children}</li>,
  hr: () => <hr className="border-slate-300 my-3" />,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3 rounded-lg border border-slate-300">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-slate-200">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-slate-50">{children}</tr>,
  th: ({ children }) => <th className="px-2 py-1.5 text-left font-semibold text-cyan-700 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">{children}</td>,
  code: ({ children }) => <code className="bg-slate-100 px-1 py-0.5 rounded text-cyan-700 text-xs font-mono border border-slate-200">{children}</code>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-cyan-500 pl-3 my-2 text-slate-600 italic">{children}</blockquote>,
};

// ════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  ISOLATED: { icon: '🛡️', text: 'ISOLATED', bg: 'bg-red-500', glow: 'from-red-500/30 to-rose-500/30' },
  ANALYZING: { icon: '🔍', text: 'ANALYZING', bg: 'bg-purple-500', glow: 'from-purple-500/30 to-violet-500/30', pulse: true },
  ALERT: { icon: '⚠️', text: 'ALERT', bg: 'bg-yellow-500', glow: 'from-yellow-500/30 to-amber-500/30' },
  PROTECTED: { icon: '🤖', text: 'PROTECTED', bg: 'bg-emerald-500', glow: 'from-emerald-500/20 to-teal-500/20' },
};

// Protocol display config — drives colour-coded badges in event rows
const PROTOCOL_CONFIG = {
  IPMI: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  Redfish: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  Modbus: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  BACnet: { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300' },
  SNMP: { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' },
  DNP3: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  SSH: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
};

const PROTOCOL_BADGE_DEFAULT = { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' };

// Fallback mock assets — used when no live DC assets have been discovered yet
const MOCK_DC_ASSETS = [
  { id: 'pdu-01',  role: 'PDU',             ip: '192.168.10.11',  protocols: ['Modbus', 'SNMP'], riskScore: 75, riskLevel: 'Medium', network: 'ot' },
  { id: 'pdu-02',  role: 'PDU',             ip: '192.168.10.12',  protocols: ['Modbus', 'SNMP'], riskScore: 75, riskLevel: 'Medium', network: 'ot' },
  { id: 'crac-01', role: 'CRAC',            ip: '192.168.10.20',  protocols: ['BACnet'],          riskScore: 65, riskLevel: 'Medium', network: 'ot' },
  { id: 'ups-01',  role: 'UPS',             ip: '192.168.10.30',  protocols: ['Modbus'],          riskScore: 70, riskLevel: 'Medium', network: 'ot' },
  { id: 'bmc-01',  role: 'BMC',             ip: '192.168.100.11', protocols: ['IPMI', 'Redfish'], riskScore: 85, riskLevel: 'High',   network: 'oob' },
  { id: 'bmc-02',  role: 'BMC',             ip: '192.168.100.12', protocols: ['IPMI', 'Redfish'], riskScore: 70, riskLevel: 'Medium', network: 'oob' },
  { id: 'hyp-01',  role: 'Hypervisor-Host', ip: '192.168.100.21', protocols: ['SSH', 'Redfish'],  riskScore: 90, riskLevel: 'High',   network: 'oob' },
  { id: 'hyp-02',  role: 'Hypervisor-Host', ip: '192.168.100.22', protocols: ['SSH', 'Redfish'],  riskScore: 80, riskLevel: 'High',   network: 'oob' },
];

// Mock site map for the dropdown when no real DC_ agents are registered.
// Mirrors the Aligned Data Centers portfolio used in DCFleet.
const _n = Date.now();
const _m = (m) => new Date(_n - m * 60000).toISOString();
const _h = (h) => new Date(_n - h * 3600000).toISOString();
const _d = (d) => new Date(_n - d * 86400000).toISOString();

const MOCK_SITES_MAP = {
  DALLAS:      { name: 'DALLAS',      otAgent: { edge_node_id: 'DC_OT_DALLAS_01',      last_heartbeat: _m(2)  }, oobAgent: { edge_node_id: 'DC_OOB_DALLAS_01',      last_heartbeat: _m(1)  } },
  ASHBURN:     { name: 'ASHBURN',     otAgent: { edge_node_id: 'DC_OT_ASHBURN_01',     last_heartbeat: _m(4)  }, oobAgent: { edge_node_id: 'DC_OOB_ASHBURN_01',     last_heartbeat: _m(3)  } },
  PHOENIX:     { name: 'PHOENIX',     otAgent: { edge_node_id: 'DC_OT_PHOENIX_01',     last_heartbeat: _m(6)  }, oobAgent: { edge_node_id: 'DC_OOB_PHOENIX_01',     last_heartbeat: _m(5)  } },
  CHICAGO:     { name: 'CHICAGO',     otAgent: { edge_node_id: 'DC_OT_CHICAGO_01',     last_heartbeat: _m(7)  }, oobAgent: { edge_node_id: 'DC_OOB_CHICAGO_01',     last_heartbeat: _h(4)  } }, // OOB offline
  SALTLAKE:    { name: 'SALTLAKE',    otAgent: { edge_node_id: 'DC_OT_SALTLAKE_01',    last_heartbeat: _m(9)  }, oobAgent: { edge_node_id: 'DC_OOB_SALTLAKE_01',    last_heartbeat: _m(8)  } },
  FREDERICK:   { name: 'FREDERICK',   otAgent: { edge_node_id: 'DC_OT_FREDERICK_01',   last_heartbeat: _m(11) }, oobAgent: null }, // OOB not deployed
  PORTLAND:    { name: 'PORTLAND',    otAgent: { edge_node_id: 'DC_OT_PORTLAND_01',    last_heartbeat: _m(5)  }, oobAgent: { edge_node_id: 'DC_OOB_PORTLAND_01',    last_heartbeat: _m(4)  } },
  SAOPAULO:    { name: 'SAOPAULO',    otAgent: { edge_node_id: 'DC_OT_SAOPAULO_01',    last_heartbeat: _m(3)  }, oobAgent: { edge_node_id: 'DC_OOB_SAOPAULO_01',    last_heartbeat: _m(2)  } },
  BOGOTA:      { name: 'BOGOTA',      otAgent: { edge_node_id: 'DC_OT_BOGOTA_01',      last_heartbeat: _h(6)  }, oobAgent: { edge_node_id: 'DC_OOB_BOGOTA_01',      last_heartbeat: _m(8)  } }, // OT offline
  QUERETARO:   { name: 'QUERETARO',   otAgent: { edge_node_id: 'DC_OT_QUERETARO_01',   last_heartbeat: _m(10) }, oobAgent: { edge_node_id: 'DC_OOB_QUERETARO_01',   last_heartbeat: _m(9)  } },
  SANTIAGO:    { name: 'SANTIAGO',    otAgent: { edge_node_id: 'DC_OT_SANTIAGO_01',    last_heartbeat: _m(6)  }, oobAgent: { edge_node_id: 'DC_OOB_SANTIAGO_01',    last_heartbeat: _m(5)  } },
  BUENOSAIRES: { name: 'BUENOSAIRES', otAgent: { edge_node_id: 'DC_OT_BUENOSAIRES_01', last_heartbeat: _d(1)  }, oobAgent: { edge_node_id: 'DC_OOB_BUENOSAIRES_01', last_heartbeat: _d(1)  } }, // both offline
  LIMA:        { name: 'LIMA',        otAgent: { edge_node_id: 'DC_OT_LIMA_01',        last_heartbeat: _m(7)  }, oobAgent: { edge_node_id: 'DC_OOB_LIMA_01',        last_heartbeat: _m(6)  } },
};

const MOCK_SITE_CITIES = {
  DALLAS: 'Dallas / Plano, TX', ASHBURN: 'Ashburn / Sterling, VA', PHOENIX: 'Phoenix, AZ',
  CHICAGO: 'Northlake, IL', SALTLAKE: 'West Jordan, UT', FREDERICK: 'Frederick, MD',
  PORTLAND: 'Portland, OR', SAOPAULO: 'São Paulo, BR', BOGOTA: 'Bogotá, CO',
  QUERETARO: 'Querétaro, MX', SANTIAGO: 'Santiago, CL', BUENOSAIRES: 'Buenos Aires, AR',
  LIMA: 'Lima, PE',
};
// Converts edge_node_id like DC_OT_AUSTIN_01 → { site: 'AUSTIN', plane: 'ot' }
// ════════════════════════════════════════════════════════════════════════════

const parseDcNodeId = (id = '') => {
  if (!id.toUpperCase().startsWith('DC_')) return null;
  const parts = id.toUpperCase().split('_');
  // Expected: DC_<PLANE>_<SITE>_<NUM>  e.g. DC_OT_AUSTIN_01
  if (parts.length >= 4) {
    const plane = parts[1].toLowerCase(); // 'ot' or 'oob'
    const site = parts.slice(2, parts.length - 1).join('_'); // 'AUSTIN'
    return { plane, site };
  }
  // Fallback: DC_<SITE>_<NUM>
  if (parts.length === 3) {
    return { plane: 'ot', site: parts[1] };
  }
  return null;
};

/**
 * Group edge nodes into DC sites.
 * Returns: Map<siteName, { otAgent: node|null, oobAgent: node|null }>
 */
const groupBySite = (edgeNodes) => {
  const sites = {};
  edgeNodes.forEach(node => {
    const parsed = parseDcNodeId(node.edge_node_id);
    if (!parsed) return;
    const { site, plane } = parsed;
    if (!sites[site]) sites[site] = { name: site, otAgent: null, oobAgent: null };
    if (plane === 'oob') sites[site].oobAgent = node;
    else sites[site].otAgent = node;
  });
  return sites;
};

const isNodeOnline = (node) => {
  if (!node?.last_heartbeat) return false;
  let ts = node.last_heartbeat;
  if (!ts.endsWith('Z') && !ts.includes('+')) ts += 'Z';
  return (Date.now() - new Date(ts).getTime()) / 60000 <= 15;
};

const getNodeStatus = (node) => (isNodeOnline(node) ? 'Online' : 'Offline');

// ════════════════════════════════════════════════════════════════════════════
// NIMBUS API
// ════════════════════════════════════════════════════════════════════════════

const executeNimbusAction = async (actionType, params) => {
  try {
    const response = await api.post('/nimbus/action/', { action_type: actionType, params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ════════════════════════════════════════════════════════════════════════════
// HEURISTIC THREAT DETECTION
// ════════════════════════════════════════════════════════════════════════════

const detectThreatsHeuristic = (events) => {
  const threats = [];
  const bySource = {};

  events.forEach(event => {
    const sourceIp = event.source_ip;
    const destIp = event.destination_ip;
    if (!sourceIp || sourceIp === 'N/A' || !destIp || destIp === 'N/A') return;

    if (!bySource[sourceIp]) {
      bySource[sourceIp] = {
        targets: new Set(), criticalCount: 0, policies: new Set(),
        events: [], agentId: null, protocols: new Set(),
      };
    }

    const s = bySource[sourceIp];
    s.targets.add(destIp);
    s.events.push(event);
    if (event.details?.policy_name) s.policies.add(event.details.policy_name);
    if (event.severity === 'Critical') s.criticalCount++;
    if (event.celerway_edge_node_id) s.agentId = event.celerway_edge_node_id;
    if (event.protocol) s.protocols.add(event.protocol);
  });

  Object.entries(bySource).forEach(([sourceIp, data]) => {
    const policies = Array.from(data.policies);
    const hasUnauthorized = policies.some(p =>
      p.toLowerCase().includes('unauthorized') || p.toLowerCase().includes('critical infrastructure')
    );

    if (data.criticalCount > 0 && hasUnauthorized) {
      threats.push({
        id: `isolation-${sourceIp}`, type: 'ISOLATION', device: sourceIp,
        targetDevices: Array.from(data.targets), reason: 'Unauthorized access blocked',
        confidence: Math.min(98, 85 + data.criticalCount * 3), severity: 'critical',
        agentId: data.agentId, protocols: Array.from(data.protocols),
      });
      if (data.agentId) {
        threats.push({
          id: `response-${sourceIp}`, type: 'VPN_ACTION', device: sourceIp,
          targetDevices: Array.from(data.targets), reason: 'Recommend network isolation response',
          confidence: 95, severity: 'critical', agentId: data.agentId,
          actionDetails: { actionType: 'Tag Assignment', tagPrefix: 'aisentry-isolated' },
        });
      }
    } else if (data.targets.size >= 3 && data.events.length >= 10) {
      threats.push({
        id: `scan-${sourceIp}`, type: 'RECOMMENDATION', device: sourceIp,
        targetDevices: Array.from(data.targets), reason: 'Port scan pattern detected',
        confidence: Math.min(90, 70 + data.targets.size * 4), severity: 'high',
        agentId: data.agentId, protocols: Array.from(data.protocols),
      });
    }
  });

  return threats.sort((a, b) => {
    const o = { critical: 0, high: 1, medium: 2 };
    return (o[a.severity] || 3) - (o[b.severity] || 3);
  });
};

const getDeviceAIStatus = (deviceIp, threats) => {
  const asSource = threats.find(t => t.device === deviceIp);
  if (asSource?.type === 'ISOLATION') return AI_STATUS.ISOLATED;
  if (asSource?.type === 'RECOMMENDATION') return AI_STATUS.ALERT;
  if (threats.find(t => t.targetDevices?.includes(deviceIp) && t.type === 'ISOLATION')) return AI_STATUS.ANALYZING;
  return AI_STATUS.PROTECTED;
};

// Detect which network plane an event belongs to based on protocol
const getEventPlane = (event) => {
  const proto = (event.protocol || '').toUpperCase();
  const oobProtos = ['IPMI', 'REDFISH', 'IDRAC', 'ILO'];
  const otProtos = ['MODBUS', 'BACNET', 'SNMP', 'DNP3'];
  if (oobProtos.some(p => proto.includes(p))) return 'oob';
  if (otProtos.some(p => proto.includes(p))) return 'ot';
  // Fallback: check agent ID plane
  const agentId = (event.celerway_edge_node_id || '').toUpperCase();
  if (agentId.includes('_OOB_')) return 'oob';
  return 'ot';
};

// ════════════════════════════════════════════════════════════════════════════
// AI BRIEFING API CALLS
// ════════════════════════════════════════════════════════════════════════════

const generateAIBriefing = async () => {
  try {
    const response = await api.post('/chat/', {
      message: `Generate a brief DC security briefing (2-3 sentences max).
Start with "Attention needed:" if there are critical threats, or "All clear:" if secure.
Focus on: BMC/OOB threats, facility control anomalies (BACnet/Modbus), or cross-network correlations.
Be direct and actionable.`,
      history: [],
      session_id: `briefing-${Date.now()}`,
    });
    const content = response.data.content;
    if (content?.includes('encountered an error')) return null;
    return content || null;
  } catch { return null; }
};

const generateExpandedBriefing = async () => {
  try {
    const response = await api.post('/chat/', {
      message: `Generate a detailed DC security analysis. Include:
1. **Threat Summary**: Active BMC/OOB or facility attacks
2. **Affected Assets**: Which BMCs, PDUs, CRACs, or hypervisors
3. **Attack Timeline**: When did activity start and what is the pattern
4. **Risk Assessment**: Criticality assessment
5. **Recommended Actions**: Immediate steps

Be specific with IP addresses, protocols (IPMI, Redfish, BACnet, Modbus), and timestamps.`,
      history: [],
      session_id: `expanded-${Date.now()}`,
    });
    const content = response.data.content;
    if (content?.includes('encountered an error')) return null;
    return content || null;
  } catch { return null; }
};

// ════════════════════════════════════════════════════════════════════════════
// NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const CloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-500 !border-2 !border-white" />
    <div className="relative bg-white border-2 border-cyan-400 rounded-2xl p-5 min-w-[220px] shadow-xl"
      style={{ boxShadow: '0 4px 20px rgba(6, 182, 212, 0.2)' }}>
      <div className="absolute -top-1.5 -right-1.5">
        <span className="absolute inline-flex h-4 w-4 rounded-full bg-emerald-500 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500" />
      </div>
      <div className="flex items-center justify-center mb-3">
        <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
          <svg className="w-7 h-7 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      <div className="text-center">
        <div className="text-sm font-semibold text-slate-800">{data.label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{data.sublabel}</div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-200 flex justify-center gap-5">
        <div className="text-center">
          <div className="text-base font-bold text-emerald-600">{data.siteCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase">Sites</div>
        </div>
        <div className="text-center">
          <div className="text-base font-bold text-blue-600">{data.agentCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase">Agents</div>
        </div>
        <div className="text-center">
          <div className="text-base font-bold text-purple-600">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase">Assets</div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-[10px] text-emerald-600 font-medium">DC Sentry Active</span>
      </div>
    </div>
  </div>
);

// Agent node — one per plane (OT or OOB) per site
const DCAgentNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  const isOob = data.plane === 'oob';

  const planeColor = isOob
    ? 'from-purple-600 to-indigo-600'
    : 'from-orange-500 to-amber-500';

  const planeBadge = isOob
    ? { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', label: 'OOB Agent' }
    : { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', label: 'OT Agent' };

  const protocols = isOob
    ? ['IPMI', 'Redfish', 'iDRAC/iLO']
    : ['Modbus', 'BACnet', 'SNMP', 'DNP3'];

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white" />

      <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden min-w-[240px] transition-all group-hover:shadow-xl border border-slate-200">
        {/* Coloured header */}
        <div className={`bg-gradient-to-r ${planeColor} px-3 py-2.5`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm">{isOob ? '🖥️' : '🔌'}</span>
              </div>
              <div>
                <div className="text-white font-bold text-xs">{data.siteName} DC Agent</div>
                <div className="text-white/80 text-[10px]">{isOob ? 'Out-of-Band Mgmt' : 'Facilities Network'}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {isOnline && <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse" />}
              <span className={`text-xs font-semibold ${isOnline ? 'text-emerald-200' : 'text-red-200'}`}>
                {isOnline ? 'LIVE' : 'OFFLINE'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {/* Plane badge */}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold border ${planeBadge.bg} ${planeBadge.text} ${planeBadge.border}`}>
            {planeBadge.label}
          </span>

          {/* Protocol pills */}
          <div className="flex flex-wrap gap-1">
            {protocols.map(p => {
              const cfg = PROTOCOL_CONFIG[p] || PROTOCOL_BADGE_DEFAULT;
              return (
                <span key={p} className={`px-1.5 py-0.5 ${cfg.bg} ${cfg.text} text-[8px] font-semibold rounded border ${cfg.border}`}>{p}</span>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-1 border-t border-slate-100">
            <span className="text-[9px] text-slate-400 font-mono">{data.nodeId || '—'}</span>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-slate-500">{data.deviceCount || 0} assets</span>
              <span className="text-[9px] text-slate-500">{data.eventCount || 0} events</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DCAssetNode = ({ data }) => {
  const aiStatus = data.aiStatus || AI_STATUS.PROTECTED;

  const roleConfig = {
    'BMC':             { icon: '🖥️', color: 'from-purple-500 to-indigo-600',  border: 'border-purple-300', bg: 'bg-purple-50' },
    'Hypervisor-Host': { icon: '⚡', color: 'from-blue-500 to-cyan-600',      border: 'border-blue-300',   bg: 'bg-blue-50' },
    'PDU':             { icon: '🔌', color: 'from-orange-500 to-amber-500',   border: 'border-orange-300', bg: 'bg-orange-50' },
    'CRAC':            { icon: '❄️', color: 'from-cyan-500 to-teal-500',      border: 'border-cyan-300',   bg: 'bg-cyan-50' },
    'UPS':             { icon: '🔋', color: 'from-emerald-500 to-green-600',  border: 'border-emerald-300',bg: 'bg-emerald-50' },
    'OOB-Switch':      { icon: '🔀', color: 'from-slate-500 to-slate-600',    border: 'border-slate-300',  bg: 'bg-slate-50' },
  };

  const cfg = roleConfig[data.role] || roleConfig['OOB-Switch'];

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-slate-400 !border-2 !border-white" />

      <div className={`relative bg-white rounded-xl shadow-md border-2 ${cfg.border} min-w-[160px] transition-all group-hover:shadow-xl`}>
        {/* AI Status */}
        <div className={`absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full text-white text-[9px] font-bold ${aiStatus.bg} flex items-center gap-1`}>
          {aiStatus.pulse && <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
          {aiStatus.icon} {aiStatus.text}
        </div>

        {/* Header */}
        <div className={`bg-gradient-to-r ${cfg.color} rounded-t-[10px] px-3 py-2 flex items-center gap-2`}>
          <span className="text-base">{cfg.icon}</span>
          <div>
            <div className="text-white font-bold text-xs">{data.role}</div>
            <div className="text-white/80 text-[9px] font-mono">{data.ip}</div>
          </div>
        </div>

        {/* Body */}
        <div className="px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[9px] text-slate-500 uppercase">Risk</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
              data.riskLevel === 'High'   ? 'bg-red-100 text-red-700 border border-red-300' :
              data.riskLevel === 'Medium' ? 'bg-amber-100 text-amber-700 border border-amber-300' :
                                           'bg-emerald-100 text-emerald-700 border border-emerald-300'
            }`}>
              {data.riskScore ?? '—'} · {data.riskLevel || 'Low'}
            </span>
          </div>

          {data.protocols?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {data.protocols.slice(0, 3).map(p => {
                const pcfg = PROTOCOL_CONFIG[p] || PROTOCOL_BADGE_DEFAULT;
                return (
                  <span key={p} className={`px-1.5 py-0.5 ${pcfg.bg} ${pcfg.text} text-[8px] font-semibold rounded border ${pcfg.border}`}>{p}</span>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between pt-1 border-t border-slate-100">
            <span className="text-[9px] text-slate-500">{data.eventCount ?? 0} events</span>
            {data.lastSeen && <span className="text-[9px] text-slate-400">{data.lastSeen}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

// Zone label node — OT Network / OOB Network dividers
const ZoneLabelNode = ({ data }) => (
  <div className={`px-5 py-2 rounded-xl border-2 border-dashed ${data.borderColor} ${data.bgColor} pointer-events-none`}>
    <div className="flex items-center gap-2">
      <span className="text-base">{data.icon}</span>
      <span className={`text-xs font-bold uppercase tracking-widest ${data.textColor}`}>{data.label}</span>
    </div>
    {data.sublabel && (
      <div className={`text-[10px] ${data.textColor} opacity-70 mt-0.5`}>{data.sublabel}</div>
    )}
  </div>
);

const nodeTypes = {
  cloud: CloudNode,
  dcAgent: DCAgentNode,
  dcAsset: DCAssetNode,
  zoneLabel: ZoneLabelNode,
};

// ════════════════════════════════════════════════════════════════════════════
// DC SITE SELECTOR
// Groups agents by site; each entry shows paired OT + OOB health
// ════════════════════════════════════════════════════════════════════════════

const DCSiteSelector = ({ sites, selectedSite, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const siteList = Object.values(sites);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm hover:border-cyan-400 transition-all min-w-[240px]"
      >
        <div className="flex flex-col items-start flex-1">
          <span className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">DC Site</span>
          <span className="text-sm font-semibold text-slate-800">
            {selectedSite ? `${selectedSite} Data Center` : 'All Sites'}
          </span>
        </div>
        {selectedSite && sites[selectedSite] && (
          <div className="flex items-center gap-1.5">
            {/* OT dot */}
            <div className="flex items-center gap-1">
              <span className="text-[9px] text-orange-600 font-medium">OT</span>
              <div className={`w-2 h-2 rounded-full ${
                sites[selectedSite].otAgent
                  ? (isNodeOnline(sites[selectedSite].otAgent) ? 'bg-emerald-500' : 'bg-red-400')
                  : 'bg-slate-300'
              }`} />
            </div>
            {/* OOB dot */}
            <div className="flex items-center gap-1">
              <span className="text-[9px] text-purple-600 font-medium">OOB</span>
              <div className={`w-2 h-2 rounded-full ${
                sites[selectedSite].oobAgent
                  ? (isNodeOnline(sites[selectedSite].oobAgent) ? 'bg-emerald-500' : 'bg-red-400')
                  : 'bg-slate-300'
              }`} />
            </div>
          </div>
        )}
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden">
            {/* All Sites option */}
            <button
              onClick={() => { onSelect(null); setIsOpen(false); }}
              className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 border-b border-slate-100 ${!selectedSite ? 'bg-cyan-50 text-cyan-700' : 'text-slate-700'}`}
            >
              All Sites ({siteList.length})
            </button>

            {siteList.length === 0 && (
              <div className="px-4 py-3 text-sm text-slate-400 italic">No DC agents registered yet</div>
            )}

            {siteList.map(site => {
              const otOnline = site.otAgent ? isNodeOnline(site.otAgent) : null;
              const oobOnline = site.oobAgent ? isNodeOnline(site.oobAgent) : null;
              const isSelected = selectedSite === site.name;

              return (
                <button
                  key={site.name}
                  onClick={() => { onSelect(site.name); setIsOpen(false); }}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 ${isSelected ? 'bg-cyan-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`text-sm font-semibold ${isSelected ? 'text-cyan-700' : 'text-slate-800'}`}>
                        {site.name}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{MOCK_SITE_CITIES[site.name] || 'Data Center Site'}</div>
                    </div>
                    {/* Paired agent health indicators */}
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[8px] text-orange-500 font-bold uppercase">OT</span>
                        {site.otAgent ? (
                          <div className={`w-2.5 h-2.5 rounded-full ${otOnline ? 'bg-emerald-500' : 'bg-red-400'}`} title={otOnline ? 'Online' : 'Offline'} />
                        ) : (
                          <div className="w-2.5 h-2.5 rounded-full bg-slate-200" title="Not registered" />
                        )}
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[8px] text-purple-500 font-bold uppercase">OOB</span>
                        {site.oobAgent ? (
                          <div className={`w-2.5 h-2.5 rounded-full ${oobOnline ? 'bg-emerald-500' : 'bg-red-400'}`} title={oobOnline ? 'Online' : 'Offline'} />
                        ) : (
                          <div className="w-2.5 h-2.5 rounded-full bg-slate-200" title="Not registered" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ stats, threatsCount, correlationCount, onOpenPanel, onRequestBriefing, isRefreshing, onRefresh }) => (
  <div className="flex items-center gap-3 flex-wrap">
    <button
      onClick={onRequestBriefing}
      className="flex items-center gap-2 bg-white hover:bg-cyan-50 border border-cyan-300 rounded-lg px-3 py-2 transition-all shadow-sm"
    >
      <span className="text-base">📋</span>
      <span className="text-sm font-medium text-cyan-700">Briefing</span>
    </button>

    {/* OT Agent health */}
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-orange-200 shadow-sm">
      <div className={`w-2 h-2 rounded-full ${stats.otOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-400'}`} />
      <span className="text-xs text-orange-700 font-semibold">OT</span>
      <span className="text-[10px] text-slate-500">{stats.otOnline ? 'Online' : 'Offline'}</span>
    </div>

    {/* OOB Agent health */}
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-purple-200 shadow-sm">
      <div className={`w-2 h-2 rounded-full ${stats.oobOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-400'}`} />
      <span className="text-xs text-purple-700 font-semibold">OOB</span>
      <span className="text-[10px] text-slate-500">{stats.oobOnline ? 'Online' : 'Offline'}</span>
    </div>

    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm">
      <span className="text-blue-600 font-bold text-sm">{stats.totalDevices || 0}</span>
      <span className="text-slate-500 text-xs">DC Assets</span>
    </div>

    {(stats.alertCount || 0) > 0 && (
      <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2 border border-red-200 animate-pulse">
        <span className="text-red-600 font-bold text-sm">{stats.alertCount}</span>
        <span className="text-red-500 text-xs">Alerts</span>
      </div>
    )}

    {/* Refresh */}
    <button
      onClick={onRefresh}
      disabled={isRefreshing}
      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
      title="Refresh"
    >
      <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    </button>

    {/* AI Panel trigger */}
    <button
      onClick={onOpenPanel}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 border transition-all shadow-sm ${
        correlationCount > 0 ? 'bg-red-50 border-red-300 hover:bg-red-100' :
        threatsCount > 0 ? 'bg-purple-50 border-purple-300 hover:bg-purple-100' :
        'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
      }`}
    >
      <span className="text-base">🤖</span>
      <div className="text-left">
        <div className={`font-bold text-sm ${
          correlationCount > 0 ? 'text-red-700' :
          threatsCount > 0 ? 'text-purple-700' :
          'text-emerald-700'
        }`}>DC Sentry</div>
        <div className={`text-[10px] ${
          correlationCount > 0 ? 'text-red-500' :
          threatsCount > 0 ? 'text-purple-500' :
          'text-emerald-500'
        }`}>
          {correlationCount > 0 ? `${correlationCount} correlations` :
           threatsCount > 0 ? `${threatsCount} detections` :
           'All clear'}
        </div>
      </div>
      {(correlationCount > 0 || threatsCount > 0) && (
        <span className={`w-5 h-5 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse ${correlationCount > 0 ? 'bg-red-500' : 'bg-purple-500'}`}>
          {correlationCount || threatsCount}
        </span>
      )}
    </button>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// CORRELATION ALERT BANNER
// Appears on left side when a CORR rule fires — cross-network alert
// ════════════════════════════════════════════════════════════════════════════

const CorrelationBanner = ({ correlationEvents, onViewDetails }) => {
  if (!correlationEvents || correlationEvents.length === 0) return null;
  const top = correlationEvents[0];
  const details = top.details || {};

  return (
    <div className="absolute left-6 top-[28%] z-30 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-50 to-rose-50 backdrop-blur-xl border-2 border-red-400 rounded-2xl shadow-2xl p-4 max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-red-200">
            <span className="text-xl">🔗</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-700 font-bold text-sm">Cross-Network Correlation</span>
            </div>
            <div className="text-slate-700 text-xs font-medium mb-1">
              {details.correlation_rule_id || 'CORR'} — {details.correlation_rule_name || top.description || 'Multi-plane alert'}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-1.5 py-0.5 bg-orange-100 border border-orange-300 rounded text-[9px] text-orange-700 font-semibold">OT</span>
              <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span className="px-1.5 py-0.5 bg-purple-100 border border-purple-300 rounded text-[9px] text-purple-700 font-semibold">OOB</span>
              {details.confidence && (
                <span className="text-[9px] text-slate-500">{details.confidence}% confidence</span>
              )}
            </div>
            <button
              onClick={onViewDetails}
              className="mt-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              View Correlation Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROTOCOL BADGE (inline)
// ════════════════════════════════════════════════════════════════════════════

const ProtocolBadge = ({ protocol }) => {
  if (!protocol) return null;
  const cfg = PROTOCOL_CONFIG[protocol] || PROTOCOL_BADGE_DEFAULT;
  return (
    <span className={`px-1.5 py-0.5 ${cfg.bg} ${cfg.text} text-[9px] font-semibold rounded border ${cfg.border}`}>
      {protocol}
    </span>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// DC EVENT ROW — protocol-aware display
// ════════════════════════════════════════════════════════════════════════════

const DCEventRow = ({ event }) => {
  const plane = getEventPlane(event);
  const severity = event.severity;
  const severityStyle = {
    Critical: 'bg-red-50 border-red-300',
    Warning:  'bg-amber-50 border-amber-200',
    Info:     'bg-slate-50 border-slate-200',
  }[severity] || 'bg-slate-50 border-slate-200';

  // Extract DC-specific detail from event.details
  const details = event.details || {};
  const subtext = details.policy_name || details.rule_name || event.description || 'Security event detected';

  // Augment function code / auth type if present
  const fnCode = details.function_code ? ` · FC${details.function_code}` : '';
  const authType = details.auth_type ? ` · Auth ${details.auth_type}` : '';
  const extraDetail = fnCode || authType || '';

  return (
    <div className={`rounded-lg border p-2.5 ${severityStyle}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <ProtocolBadge protocol={event.protocol} />
          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
            plane === 'oob'
              ? 'bg-purple-100 text-purple-700 border border-purple-200'
              : 'bg-orange-100 text-orange-700 border border-orange-200'
          }`}>
            {plane.toUpperCase()}
          </span>
        </div>
        <span className="text-[9px] text-slate-400 shrink-0 ml-1">
          {event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-xs mt-1">
        <span className="font-mono text-slate-600">{event.source_ip || 'N/A'}</span>
        <span className="text-slate-400">→</span>
        <span className="font-mono text-slate-700">{event.destination_ip || 'N/A'}</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 truncate">
        {subtext}{extraDetail}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CORRELATION CARD
// ════════════════════════════════════════════════════════════════════════════

const CorrelationCard = ({ event }) => {
  const details = event.details || {};
  const sc = {
    'Critical': { border: 'border-red-400',    bg: 'bg-red-50',    badge: 'bg-red-500',    text: 'text-red-700' },
    'High':     { border: 'border-amber-400',  bg: 'bg-amber-50',  badge: 'bg-amber-500',  text: 'text-amber-700' },
    'Medium':   { border: 'border-yellow-400', bg: 'bg-yellow-50', badge: 'bg-yellow-500', text: 'text-yellow-700' },
  }[event.severity] || { border: 'border-yellow-400', bg: 'bg-yellow-50', badge: 'bg-yellow-500', text: 'text-yellow-700' };

  return (
    <div className={`border-2 ${sc.border} ${sc.bg} rounded-xl p-4`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center border border-red-200">
            <span className="text-base">🔗</span>
          </div>
          <div>
            <div className="text-slate-800 font-semibold text-sm">{details.correlation_rule_name || 'Correlation Alert'}</div>
            <div className={`text-xs font-mono ${sc.text}`}>{details.correlation_rule_id || '—'}</div>
          </div>
        </div>
        {details.confidence && (
          <div className="text-right">
            <div className="text-slate-400 text-[10px]">Confidence</div>
            <div className="text-slate-800 font-bold">{details.confidence}%</div>
          </div>
        )}
      </div>

      {/* Cross-network planes */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`px-2 py-0.5 rounded-full text-white text-[9px] font-bold ${sc.badge}`}>{event.severity}</span>
        <span className="px-1.5 py-0.5 bg-orange-100 border border-orange-300 rounded text-[9px] text-orange-700 font-semibold">OT</span>
        <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
        <span className="px-1.5 py-0.5 bg-purple-100 border border-purple-300 rounded text-[9px] text-purple-700 font-semibold">OOB</span>
        {details.zone_id && (
          <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[9px] text-slate-600 font-mono">Zone: {details.zone_id}</span>
        )}
      </div>

      <p className="text-slate-600 text-xs leading-relaxed mb-3">{details.description || event.description || ''}</p>

      {/* Trigger event IDs */}
      {(details.trigger_a_event_id || details.trigger_b_event_id) && (
        <div className="bg-white rounded-lg border border-slate-200 p-2 space-y-1">
          <div className="text-[9px] text-slate-500 uppercase font-semibold">Correlated Events</div>
          {details.trigger_a_event_id && (
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-orange-600 font-medium">OT →</span>
              <span className="font-mono text-[9px] text-slate-500 truncate">{details.trigger_a_event_id}</span>
            </div>
          )}
          {details.trigger_b_event_id && (
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-purple-600 font-medium">OOB →</span>
              <span className="font-mono text-[9px] text-slate-500 truncate">{details.trigger_b_event_id}</span>
            </div>
          )}
        </div>
      )}

      {details.fired_at && (
        <div className="mt-2 text-[9px] text-slate-400 text-right">
          Fired: {new Date(details.fired_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// AI PANEL — 4 tabs: Correlations, OOB Threats, OT Threats, Events
// ════════════════════════════════════════════════════════════════════════════

const AIPanel = ({ isOpen, onClose, threats, allEvents, correlationEvents, onRefresh, isRefreshing, onApproveVPN }) => {
  const [activeTab, setActiveTab] = useState('correlations');
  const [executingAction, setExecutingAction] = useState(null);
  const [actionResults, setActionResults] = useState({});

  if (!isOpen) return null;

  const oobThreats = threats.filter(t => t.type !== 'VPN_ACTION' &&
    (t.protocols?.some(p => ['IPMI', 'Redfish'].includes(p)) ||
     (t.agentId || '').toUpperCase().includes('_OOB_')));
  const otThreats = threats.filter(t => t.type !== 'VPN_ACTION' && !oobThreats.includes(t));
  const vpnActions = threats.filter(t => t.type === 'VPN_ACTION');

  const oobEvents = allEvents.filter(e => getEventPlane(e) === 'oob').slice(0, 20);
  const otEvents  = allEvents.filter(e => getEventPlane(e) === 'ot').slice(0, 20);
  const allDcEvents = allEvents.slice(0, 30);

  const handleApproveVPN = async (action) => {
    setExecutingAction(action.id);
    try {
      const result = await onApproveVPN(action);
      setActionResults(prev => ({ ...prev, [action.id]: result }));
    } catch (error) {
      setActionResults(prev => ({ ...prev, [action.id]: { success: false, error: error.message } }));
    } finally {
      setExecutingAction(null);
    }
  };

  const tabs = [
    {
      id: 'correlations',
      label: '🔗 Correlations',
      count: correlationEvents.length,
      urgent: correlationEvents.length > 0,
    },
    {
      id: 'oob',
      label: '🖥️ OOB Threats',
      count: oobThreats.length,
      urgent: false,
    },
    {
      id: 'ot',
      label: '🔌 OT Threats',
      count: otThreats.length,
      urgent: false,
    },
    {
      id: 'events',
      label: '📊 Events',
      count: allDcEvents.length,
      urgent: false,
    },
  ];

  const ThreatCard = ({ threat }) => (
    <div className={`border-2 rounded-xl p-4 ${
      threat.severity === 'critical' ? 'border-red-400 bg-red-50' : 'border-yellow-400 bg-yellow-50'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{threat.severity === 'critical' ? '🛡️' : '⚠️'}</span>
          <div>
            <div className="text-slate-800 font-semibold text-sm">{threat.type}</div>
            <div className="text-slate-500 text-xs">Heuristic detection</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-slate-400 text-[10px]">Confidence</div>
          <div className="text-slate-800 font-bold">{threat.confidence}%</div>
        </div>
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        <span className="font-mono text-sm bg-white px-2 py-1 rounded text-cyan-700 border border-slate-200">{threat.device}</span>
        {threat.protocols?.map(p => <ProtocolBadge key={p} protocol={p} />)}
      </div>
      <div className="text-slate-700 text-sm">{threat.reason}</div>
      <div className="text-slate-500 text-xs mt-1">
        Targeting: {threat.targetDevices?.slice(0, 3).join(', ')}{threat.targetDevices?.length > 3 ? ` +${threat.targetDevices.length - 3} more` : ''}
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[440px] bg-white border-l border-slate-200 z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center">
              <span className="text-lg">🤖</span>
            </div>
            <div>
              <div className="text-white font-bold">DC Sentry Activity</div>
              <div className="text-slate-400 text-xs">
                {correlationEvents.length > 0 ? `${correlationEvents.length} cross-network correlations` : 'Real-time DC threat detection'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onRefresh} disabled={isRefreshing} className="p-2 text-slate-400 hover:text-white rounded-lg disabled:opacity-50">
              <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg">✕</button>
          </div>
        </div>

        {/* Correlation warning bar */}
        {correlationEvents.length > 0 && (
          <div className="bg-red-500 px-5 py-2 flex items-center gap-2 flex-shrink-0">
            <span className="w-2 h-2 bg-white rounded-full animate-ping" />
            <span className="text-white text-xs font-semibold">
              {correlationEvents.length} cross-network correlation{correlationEvents.length > 1 ? 's' : ''} active — OT + OOB signals matched
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 flex-shrink-0 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-3 py-3 text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                activeTab === tab.id
                  ? (tab.urgent ? 'text-red-600 border-b-2 border-red-500 bg-red-50' : 'text-cyan-600 border-b-2 border-cyan-500 bg-cyan-50')
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] text-white font-bold ${tab.urgent ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* ── Correlations ── */}
          {activeTab === 'correlations' && (
            correlationEvents.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">🔗</span>
                <p className="text-slate-700 font-medium mt-4">No Correlation Alerts</p>
                <p className="text-slate-500 text-sm mt-1">Cross-network alerts appear here when OT and OOB signals match within the correlation window</p>
                <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200 text-left">
                  <div className="text-xs font-semibold text-slate-600 mb-2">DC Correlation Rules</div>
                  {[
                    { id: 'CORR-001', desc: 'Physical intrusion + BMC login from new IP' },
                    { id: 'CORR-002', desc: 'OT anomaly + simultaneous OOB admin activity' },
                    { id: 'CORR-003', desc: 'Physical access badge + unauthorized remote session' },
                  ].map(r => (
                    <div key={r.id} className="flex items-start gap-2 mb-1.5">
                      <span className="text-[9px] font-mono text-purple-600 font-bold mt-0.5">{r.id}</span>
                      <span className="text-[10px] text-slate-500">{r.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              correlationEvents.map((event, idx) => <CorrelationCard key={event.event_id || idx} event={event} />)
            )
          )}

          {/* ── OOB Threats ── */}
          {activeTab === 'oob' && (
            <>
              {oobThreats.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-3xl">🖥️</span>
                  <p className="text-slate-700 font-medium mt-3">OOB Network Clear</p>
                  <p className="text-slate-500 text-sm mt-1">No unauthorized BMC or hypervisor activity detected</p>
                </div>
              ) : (
                oobThreats.map(t => <ThreatCard key={t.id} threat={t} />)
              )}
              {oobEvents.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2">Recent OOB Events</div>
                  <div className="space-y-2">
                    {oobEvents.map((e, i) => <DCEventRow key={i} event={e} />)}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── OT Threats ── */}
          {activeTab === 'ot' && (
            <>
              {otThreats.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-3xl">🔌</span>
                  <p className="text-slate-700 font-medium mt-3">OT Network Clear</p>
                  <p className="text-slate-500 text-sm mt-1">No anomalous Modbus, BACnet, or SNMP activity detected</p>
                </div>
              ) : (
                otThreats.map(t => <ThreatCard key={t.id} threat={t} />)
              )}
              {vpnActions.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2">Recommended Actions</div>
                  {vpnActions.map(action => {
                    const result = actionResults[action.id];
                    const isExecuting = executingAction === action.id;
                    return (
                      <div key={action.id} className="border-2 border-purple-400 bg-purple-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">🔒</span>
                          <div>
                            <div className="text-slate-800 font-semibold text-sm">Network Response</div>
                            <div className="text-purple-600 text-xs">Nimbus API • Tag Assignment</div>
                          </div>
                          <span className="ml-auto text-slate-700 font-bold text-sm">{action.confidence}%</span>
                        </div>
                        <div className="mb-3 bg-white rounded-lg p-2 border border-slate-200">
                          <div className="text-[10px] text-slate-400 uppercase mb-1">Target Router</div>
                          <div className="font-mono text-xs text-cyan-700">{action.agentId || 'Stratus5G_5728_Austin'}</div>
                        </div>
                        <div className="mb-3 bg-white rounded-lg p-2 border border-purple-200">
                          <div className="text-[10px] text-slate-400 uppercase mb-1">Tag to Apply</div>
                          <div className="font-mono text-xs text-purple-700">
                            aisentry-isolated-{new Date().toISOString().slice(0,10).replace(/-/g,'')}
                          </div>
                        </div>
                        {result && (
                          <div className={`mb-3 p-3 rounded-lg ${result.success ? 'bg-emerald-50 border border-emerald-300' : 'bg-red-50 border border-red-300'}`}>
                            <div className={`text-sm font-medium ${result.success ? 'text-emerald-700' : 'text-red-700'}`}>
                              {result.success ? '✅ Tag Applied' : `❌ Failed: ${result.error}`}
                            </div>
                          </div>
                        )}
                        {!result?.success && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApproveVPN(action)}
                              disabled={isExecuting}
                              className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 disabled:from-slate-400 disabled:to-slate-400 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2"
                            >
                              {isExecuting ? <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</> : '✓ Approve & Execute'}
                            </button>
                            <button disabled={isExecuting} className="px-3 py-2 bg-white hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-lg border border-slate-200">Dismiss</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {otEvents.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2">Recent OT Events</div>
                  <div className="space-y-2">
                    {otEvents.map((e, i) => <DCEventRow key={i} event={e} />)}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── All Events ── */}
          {activeTab === 'events' && (
            allDcEvents.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">📊</span>
                <p className="text-slate-700 font-medium mt-4">No Events</p>
                <p className="text-slate-500 text-sm mt-1">DC events will stream in as agents report</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allDcEvents.map((e, i) => <DCEventRow key={i} event={e} />)}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gradient-to-br from-cyan-500 to-blue-500 rounded flex items-center justify-center">
              <span className="text-[9px] font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-500">Powered by <span className="text-cyan-600 font-semibold">DC Sentry</span></span>
          </div>
          <span className="text-[10px] text-slate-400">Polling every 30s</span>
        </div>
      </div>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING MODAL
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingModal = ({ isOpen, onClose, aiBriefing, stats, threats, isLoadingAI, onRequestExpanded, expandedBriefing, isLoadingExpanded }) => {
  const [showExpanded, setShowExpanded] = useState(false);
  if (!isOpen) return null;

  const criticalCount = threats.filter(t => t.severity === 'critical').length;
  const hasCritical = criticalCount > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className={`bg-gradient-to-r ${hasCritical ? 'from-red-500 to-orange-500' : 'from-cyan-500 to-blue-500'} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-2xl">{hasCritical ? '🚨' : '🤖'}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">DC Shift Briefing</h2>
              <p className="text-white/80 text-sm">Data Center Security Summary — OT + OOB Networks</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className={`text-xl font-bold ${stats.otOnline ? 'text-emerald-600' : 'text-red-500'}`}>{stats.otOnline ? '●' : '○'}</div>
              <div className="text-xs text-slate-500 mt-1">OT Agent</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className={`text-xl font-bold ${stats.oobOnline ? 'text-emerald-600' : 'text-red-500'}`}>{stats.oobOnline ? '●' : '○'}</div>
              <div className="text-xs text-slate-500 mt-1">OOB Agent</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center border border-slate-100">
              <div className="text-xl font-bold text-blue-600">{stats.totalDevices || 0}</div>
              <div className="text-xs text-slate-500 mt-1">DC Assets</div>
            </div>
            <div className={`rounded-lg p-3 text-center border ${hasCritical ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-100'}`}>
              <div className={`text-xl font-bold ${hasCritical ? 'text-red-600' : 'text-slate-500'}`}>{criticalCount}</div>
              <div className="text-xs text-slate-500 mt-1">Threats</div>
            </div>
          </div>

          {/* AI Briefing */}
          <div className={`rounded-xl p-4 ${hasCritical ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🤖</span>
              <span className="font-semibold text-slate-800 text-sm">AI Security Analysis</span>
            </div>
            {isLoadingAI ? (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-slate-500 text-sm">Analysing DC security posture...</span>
              </div>
            ) : aiBriefing ? (
              <div>
                <p className="text-slate-700 text-sm leading-relaxed">{aiBriefing}</p>
                {!showExpanded && (
                  <button
                    onClick={() => { setShowExpanded(true); onRequestExpanded(); }}
                    className="mt-3 text-cyan-600 hover:text-cyan-700 text-xs font-medium"
                  >
                    Generate detailed analysis →
                  </button>
                )}
                {showExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    {isLoadingExpanded ? (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-slate-500 text-sm">Generating detailed analysis...</span>
                      </div>
                    ) : expandedBriefing ? (
                      <div className="max-h-72 overflow-y-auto pr-1">
                        <ReactMarkdown components={MarkdownComponents} remarkPlugins={[remarkGfm]}>{expandedBriefing}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">Unable to generate expanded analysis.</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">AI analysis unavailable — check backend connectivity.</p>
            )}
          </div>
        </div>

        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-md flex items-center justify-center">
              <span className="text-xs font-bold text-white">e</span>
            </div>
            <span className="text-xs text-slate-500">Powered by <span className="text-cyan-600 font-semibold">DC Sentry</span></span>
          </div>
          <button
            onClick={onClose}
            className={`px-6 py-2 ${hasCritical ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600' : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'} text-white font-semibold rounded-lg transition-all`}
          >
            {hasCritical ? 'Review Threats' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CHAT PANEL (preserved from v1 — session-based AI chat)
// ════════════════════════════════════════════════════════════════════════════

const createNewSession = (idSuffix = '') => {
  const ts = Date.now();
  return {
    id: `dc-sentry-${ts}${idSuffix}`,
    title: `Chat ${new Date(ts).toLocaleDateString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
    messages: [],
    createdAt: ts,
  };
};

const DC_STORAGE_KEY = 'dcSentryChatSessions';
const DC_CURRENT_SESSION_KEY = 'dcSentryCurrentSession';

const ChatPanel = ({ threats, stats, selectedSite, corr004Data }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const MIN_INTERVAL = 10000;

  useEffect(() => {
    let initialSessions = [];
    let initialCurrentId = null;
    try {
      const stored = localStorage.getItem(DC_STORAGE_KEY);
      const storedId = localStorage.getItem(DC_CURRENT_SESSION_KEY);
      if (stored) initialSessions = JSON.parse(stored);
      if (initialSessions.length > 0) {
        initialCurrentId = (storedId && initialSessions.find(s => s.id === storedId)) ? storedId : [...initialSessions].sort((a,b) => b.createdAt - a.createdAt)[0].id;
      } else {
        const s = createNewSession('-initial');
        initialSessions = [s];
        initialCurrentId = s.id;
      }
    } catch {
      const s = createNewSession('-fallback');
      initialSessions = [s];
      initialCurrentId = s.id;
    }
    setSessions(initialSessions);
    setCurrentSessionId(initialCurrentId);
  }, []);

  useEffect(() => {
    if (sessions.length > 0 && currentSessionId) {
      const t = setTimeout(() => {
        try {
          localStorage.setItem(DC_STORAGE_KEY, JSON.stringify(sessions));
          localStorage.setItem(DC_CURRENT_SESSION_KEY, currentSessionId);
        } catch {}
      }, 500);
      return () => clearTimeout(t);
    }
  }, [sessions, currentSessionId]);

  useEffect(() => {
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [sessions, currentSessionId]);

  const currentSession = useMemo(() => sessions.find(s => s.id === currentSessionId), [sessions, currentSessionId]);
  const messages = useMemo(() => currentSession?.messages || [], [currentSession]);
  const sortedSessions = useMemo(() => [...sessions].sort((a,b) => b.createdAt - a.createdAt), [sessions]);

  const buildContext = () => {
    let ctx = `Current DC Network State:\n`;
    ctx += `- OT Agent: ${stats.otOnline ? 'Online' : 'Offline'}\n`;
    ctx += `- OOB Agent: ${stats.oobOnline ? 'Online' : 'Offline'}\n`;
    ctx += `- DC Assets: ${stats.totalDevices}\n`;
    ctx += `- Alerts: ${stats.alertCount}\n`;
    if (selectedSite) ctx += `- Site: ${selectedSite}\n`;
    if (threats.length > 0) {
      ctx += `\nActive Threats:\n`;
      threats.slice(0, 5).forEach(t => {
        ctx += `- ${t.type}: ${t.device} (${t.confidence}% confidence) - ${t.reason}\n`;
      });
    }
    if (corr004Data) {
      const qd = corr004Data['queue_delay'];
      const sr = corr004Data['site_risk'];
      const pr = corr004Data['procurement_risk'];
      ctx += `\nCORR-004 Site Intelligence for ${selectedSite}:\n`;
      if (qd) ctx += `- Queue Delay Risk: ${qd.score}/100 (${qd.confidence} confidence) — ${qd.summary}\n`;
      if (sr) ctx += `- Site Risk: ${sr.score}/100 (${sr.confidence} confidence) — ${sr.summary}\n`;
      if (pr) ctx += `- Procurement Risk: ${pr.score}/100 (${pr.confidence} confidence) — ${pr.summary}\n`;
      if (qd?.score >= 75) ctx += `- CRITICAL: High interconnection queue slip risk. Engage ISO early and build 6-month buffer.\n`;
      else if (qd?.score >= 50) ctx += `- WARNING: Moderate queue delay risk. Add timeline buffer.\n`;
      ctx += `Note: CORR-004 scores are pre-computed from LBNL queue data, EIA grid capacity, NOAA climate, and BLS labor. Do NOT call queue_risk or corr004_summary tools — use the data above directly.\n`;
    }
    return ctx;
  };

  const canSend = () => !isLoading && input.trim() && (Date.now() - lastRequestTime >= MIN_INTERVAL);
  const getWaitTime = () => Math.max(0, Math.ceil((MIN_INTERVAL - (Date.now() - lastRequestTime)) / 1000));

  const handleSend = async () => {
    if (!canSend() || !currentSessionId) return;
    const userMessage = input.trim();
    const session = sessions.find(s => s.id === currentSessionId);
    if (!session) return;

    let newTitle = session.title;
    if (session.messages.length === 0) newTitle = userMessage.length > 20 ? `${userMessage.substring(0, 20)}...` : userMessage;

    const userMsg = { role: 'user', content: userMessage };
    setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...s.messages, userMsg], title: newTitle } : s));
    setInput('');
    setIsLoading(true);
    setLastRequestTime(Date.now());

    try {
      const contextualQuery = `${buildContext()}\n\nUser question: ${userMessage}`;
      const history = session.messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      const response = await sendChatQuery(contextualQuery, history, currentSessionId);
      const assistantMsg = { role: 'assistant', content: response.content || 'No response received.' };
      setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...s.messages, assistantMsg] } : s));
    } catch (error) {
      const errorMsg = { role: 'assistant', content: `Error: ${error.message}` };
      setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...s.messages, errorMsg] } : s));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const quickActions = [
    'What OOB threats are active?',
    'Summarize OT anomalies',
    'Any correlation alerts?',
    'Explain the CORR-004 risk scores for this site',
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col" style={{ width: '420px', height: '380px', position: 'relative', overflow: 'hidden' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base">🤖</span>
            <div>
              <div className="text-sm font-semibold text-slate-800">Ask DC Sentry</div>
              <div className="text-[10px] text-slate-500">{threats.length} threats • {sessions.length} session{sessions.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-1.5 px-2 py-1 text-[10px] bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
              <span className="text-slate-600 truncate max-w-[80px]">{currentSession?.title || 'Chat'}</span>
              <svg className={`w-3 h-3 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
                <button onClick={() => { const s = createNewSession(); setSessions(prev => [s,...prev]); setCurrentSessionId(s.id); setIsDropdownOpen(false); }} className="w-full px-3 py-2 text-left text-xs text-cyan-700 font-medium hover:bg-cyan-50 flex items-center gap-2 border-b border-slate-100">
                  + New Chat
                </button>
                <div className="max-h-40 overflow-y-auto">
                  {sortedSessions.map(session => (
                    <div key={session.id} onClick={() => { setCurrentSessionId(session.id); setIsDropdownOpen(false); }} className={`px-3 py-2 text-xs cursor-pointer flex items-center justify-between group ${session.id === currentSessionId ? 'bg-cyan-50 text-cyan-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                      <span className="truncate flex-1">{session.title}</span>
                      <button onClick={(e) => { e.stopPropagation(); setSessions(prev => { const r = prev.filter(s => s.id !== session.id); if (r.length === 0) { const n = createNewSession(); setCurrentSessionId(n.id); return [n]; } if (currentSessionId === session.id) setCurrentSessionId(r[0].id); return r; }); }} className="ml-2 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-slate-400 text-xs mb-3">Ask about DC infrastructure, threats, or asset risk</p>
            <div className="space-y-1">
              {quickActions.map(q => (
                <button key={q} onClick={() => { setInput(q); setTimeout(() => handleSend(), 100); }} className="block w-full text-left px-2 py-1.5 bg-slate-50 hover:bg-cyan-50 border border-slate-200 hover:border-cyan-300 rounded-lg text-xs text-slate-600 hover:text-cyan-700 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs ${msg.role === 'user' ? 'bg-cyan-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
                {msg.role === 'assistant' ? (
                  <ReactMarkdown components={MarkdownComponents} remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                ) : msg.content}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-xl px-3 py-2 flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about DC infrastructure..."
            className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!canSend()}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${canSend() ? 'bg-cyan-600 text-white hover:bg-cyan-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            {isLoading ? '…' : '→'}
          </button>
        </div>
        {lastRequestTime > 0 && getWaitTime() > 0 && (
          <div className="text-[9px] text-orange-600 mt-1 text-center">Wait {getWaitTime()}s</div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TOPOLOGY BUILDER
// Builds a split OT / OOB zone layout:
//   Cloud → [Site nodes] → [OT Agent | OOB Agent] → [OT Assets | OOB Assets]
// When multiple sites exist, each site occupies a horizontal column.
// ════════════════════════════════════════════════════════════════════════════

const buildTopology = (sites, allEvents, selectedSite, currentThreats, mockAssets) => {
  const newNodes = [];
  const newEdges = [];

  const filteredSites = selectedSite
    ? Object.values(sites).filter(s => s.name === selectedSite)
    : Object.values(sites);

  // If no real DC sites found, fall back to mock layout with a placeholder site
  const useMock = filteredSites.length === 0;
  const effectiveSites = useMock
    ? selectedSite && MOCK_SITES_MAP[selectedSite]
      ? [MOCK_SITES_MAP[selectedSite]]
      : Object.values(MOCK_SITES_MAP).slice(0, 3) // "All Sites" shows first 3 to avoid overcrowding
    : filteredSites;

  const otAssets = mockAssets.filter(a => a.network === 'ot');
  const oobAssets = mockAssets.filter(a => a.network === 'oob');
  const totalDevices = mockAssets.length;

  // Build event count map by agent
  const eventCountByAgent = {};
  allEvents.forEach(e => {
    const id = e.celerway_edge_node_id;
    if (id) eventCountByAgent[id] = (eventCountByAgent[id] || 0) + 1;
  });

  // Layout constants
  const SITE_WIDTH = 700;
  const CLOUD_Y = 0;
  const AGENT_Y = 200;
  const ASSET_Y = 430;
  const OT_X_OFFSET = -180;
  const OOB_X_OFFSET = 180;
  const ASSET_GAP = 190;

  const totalWidth = effectiveSites.length * SITE_WIDTH;
  const startX = -(totalWidth / 2) + SITE_WIDTH / 2;

  // Cloud node
  newNodes.push({
    id: 'cloud',
    type: 'cloud',
    position: { x: -110, y: CLOUD_Y },
    data: {
      label: 'AI Sentry Cloud',
      sublabel: 'ai-sentry.com',
      siteCount: effectiveSites.length,
      agentCount: effectiveSites.reduce((n, s) => n + (s.otAgent ? 1 : 0) + (s.oobAgent ? 1 : 0), 0),
      assetCount: totalDevices,
    },
  });

  // Total stats
  let totalAlerts = 0;
  allEvents.forEach(e => { if (['Warning', 'Critical'].includes(e.severity)) totalAlerts++; });

  let anyOtOnline = false, anyOobOnline = false;

  effectiveSites.forEach((site, sIdx) => {
    const siteCenterX = startX + sIdx * SITE_WIDTH;
    const otNodeId = site.otAgent?.edge_node_id || `mock-ot-${site.name}`;
    const oobNodeId = site.oobAgent?.edge_node_id || `mock-oob-${site.name}`;

    const otStatus = site.otAgent ? getNodeStatus(site.otAgent) : (useMock ? 'Online' : 'Offline');
    const oobStatus = site.oobAgent ? getNodeStatus(site.oobAgent) : (useMock ? 'Online' : 'Offline');

    if (otStatus === 'Online') anyOtOnline = true;
    if (oobStatus === 'Online') anyOobOnline = true;

    // OT Agent node
    newNodes.push({
      id: `agent-ot-${site.name}`,
      type: 'dcAgent',
      position: { x: siteCenterX + OT_X_OFFSET - 120, y: AGENT_Y },
      data: {
        plane: 'ot',
        siteName: site.name,
        nodeId: otNodeId,
        status: otStatus,
        hostname: site.otAgent?.hostname || '',
        deviceCount: otAssets.length,
        eventCount: site.otAgent ? (eventCountByAgent[otNodeId] || 0) : 0,
      },
    });

    // OOB Agent node
    newNodes.push({
      id: `agent-oob-${site.name}`,
      type: 'dcAgent',
      position: { x: siteCenterX + OOB_X_OFFSET + 80, y: AGENT_Y },
      data: {
        plane: 'oob',
        siteName: site.name,
        nodeId: oobNodeId,
        status: oobStatus,
        hostname: site.oobAgent?.hostname || '',
        deviceCount: oobAssets.length,
        eventCount: site.oobAgent ? (eventCountByAgent[oobNodeId] || 0) : 0,
      },
    });

    // Cloud → agents
    newEdges.push({
      id: `cloud-to-ot-${site.name}`,
      source: 'cloud',
      target: `agent-ot-${site.name}`,
      animated: otStatus === 'Online',
      style: { stroke: otStatus === 'Online' ? '#f97316' : '#ef4444', strokeWidth: 2, strokeDasharray: otStatus === 'Online' ? '' : '5 5' },
      label: '',
    });
    newEdges.push({
      id: `cloud-to-oob-${site.name}`,
      source: 'cloud',
      target: `agent-oob-${site.name}`,
      animated: oobStatus === 'Online',
      style: { stroke: oobStatus === 'Online' ? '#8b5cf6' : '#ef4444', strokeWidth: 2, strokeDasharray: oobStatus === 'Online' ? '' : '5 5' },
    });

    // OT Assets
    const otStartX = siteCenterX + OT_X_OFFSET - 120 - ((otAssets.length - 1) * ASSET_GAP) / 2;
    otAssets.forEach((asset, idx) => {
      const deviceId = `dc-ot-${site.name}-${asset.id}`;
      const aiStatus = getDeviceAIStatus(asset.ip, currentThreats || []);
      const evts = allEvents.filter(e => e.destination_ip === asset.ip).length;

      newNodes.push({
        id: deviceId,
        type: 'dcAsset',
        position: { x: otStartX + idx * ASSET_GAP, y: ASSET_Y },
        data: { ip: asset.ip, role: asset.role, protocols: asset.protocols, riskScore: asset.riskScore, riskLevel: asset.riskLevel, eventCount: evts, aiStatus },
      });

      let edgeColor = '#f97316';
      if (aiStatus === AI_STATUS.ISOLATED) edgeColor = '#ef4444';
      else if (aiStatus === AI_STATUS.ANALYZING) edgeColor = '#a855f7';

      newEdges.push({
        id: `ot-agent-to-${deviceId}`,
        source: `agent-ot-${site.name}`,
        target: deviceId,
        animated: otStatus === 'Online',
        style: { stroke: edgeColor, strokeWidth: 1.5 },
      });
    });

    // OOB Assets
    const oobStartX = siteCenterX + OOB_X_OFFSET + 80 - ((oobAssets.length - 1) * ASSET_GAP) / 2;
    oobAssets.forEach((asset, idx) => {
      const deviceId = `dc-oob-${site.name}-${asset.id}`;
      const aiStatus = getDeviceAIStatus(asset.ip, currentThreats || []);
      const evts = allEvents.filter(e => e.destination_ip === asset.ip).length;

      newNodes.push({
        id: deviceId,
        type: 'dcAsset',
        position: { x: oobStartX + idx * ASSET_GAP, y: ASSET_Y },
        data: { ip: asset.ip, role: asset.role, protocols: asset.protocols, riskScore: asset.riskScore, riskLevel: asset.riskLevel, eventCount: evts, aiStatus },
      });

      let edgeColor = '#8b5cf6';
      if (aiStatus === AI_STATUS.ISOLATED) edgeColor = '#ef4444';
      else if (aiStatus === AI_STATUS.ANALYZING) edgeColor = '#a855f7';

      newEdges.push({
        id: `oob-agent-to-${deviceId}`,
        source: `agent-oob-${site.name}`,
        target: deviceId,
        animated: oobStatus === 'Online',
        style: { stroke: edgeColor, strokeWidth: 1.5 },
      });
    });

    // Cross-network correlation edges (dashed red) when correlations are active
    // These are added in the main component where correlationEvents is available
  });

  return {
    nodes: newNodes,
    edges: newEdges,
    stats: {
      otOnline: anyOtOnline,
      oobOnline: anyOobOnline,
      totalAgents: effectiveSites.reduce((n, s) => n + (s.otAgent ? 1 : 0) + (s.oobAgent ? 1 : 0), 0),
      totalDevices,
      alertCount: totalAlerts,
    },
    useMock,
  };
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const DCSentry = () => {
  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([]);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Agent state
  const [edgeNodesList, setEdgeNodesList] = useState([]);
  const [dcSites, setDcSites] = useState({});         // Map<siteName, {otAgent, oobAgent}>
  const [selectedSite, setSelectedSite] = useState(null);

  // Event state
  const [allEvents, setAllEvents] = useState([]);
  const [correlationEvents, setCorrelationEvents] = useState([]);
  const [threats, setThreats] = useState([]);

  // UI state
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [showBriefing, setShowBriefing] = useState(true);
  const [aiBriefing, setAiBriefing] = useState(null);
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [useMock, setUseMock] = useState(false);
  const [stats, setStats] = useState({ otOnline: false, oobOnline: false, totalAgents: 0, totalDevices: 0, alertCount: 0 });

  const [corr004Site, setCorr004Site]       = useState(null);
  const [corr004Loading, setCorr004Loading] = useState(false);

  // ── AI Briefing ─────────────────────────────────────────────────────────

  const fetchBriefing = useCallback(async () => {
    setIsLoadingAI(true);
    const b = await generateAIBriefing();
    setAiBriefing(b);
    setIsLoadingAI(false);
  }, []);

  const fetchExpandedBriefing = useCallback(async () => {
    setIsLoadingExpanded(true);
    const b = await generateExpandedBriefing();
    setExpandedBriefing(b);
    setIsLoadingExpanded(false);
  }, []);

  const handleRequestBriefing = useCallback(() => {
    setShowBriefing(true);
    if (!aiBriefing) fetchBriefing();
  }, [aiBriefing, fetchBriefing]);

  // ── Correlation fetch ────────────────────────────────────────────────────

  const fetchCorrelationAlerts = useCallback(async () => {
    try {
      const events = await eventsService.getEvents({ limit: 50 });
      const corr = (events || []).filter(e => e.event_type?.startsWith('CorrelationAlert_'));
      setCorrelationEvents(corr);
    } catch (err) {
      console.error('Correlation fetch error:', err);
    }
  }, []);

  const fetchCorr004Site = useCallback(async (siteName) => {
    if (!siteName) return;
    setCorr004Loading(true);
    console.log('[CORR-004] fetching for siteName:', siteName);
    try {
      const base   = process.env.REACT_APP_API_URL || '';
      const apiKey = process.env.REACT_APP_API_KEY || '';
      const url    = `${base}/admin/ingest/corr004/results`;
      console.log('[CORR-004] url:', url, 'key present:', !!apiKey);
      const res = await fetch(url, {
        headers: { 'X-API-Key': apiKey },
      });
      console.log('[CORR-004] response status:', res.status);
      if (!res.ok) {
        console.warn('[CORR-004] non-ok response:', res.status);
        setCorr004Site(null);
        return;
      }
      const data = await res.json();
      console.log('[CORR-004] total results:', data.results?.length);
      const ids = [...new Set((data.results||[]).map(r=>r.edge_node_id))];
      console.log('[CORR-004] available edge_node_ids:', ids);

      const siteKey = siteName.split(' ')[0].toUpperCase();
      console.log('[CORR-004] matching siteKey:', siteKey);
      const rows = (data.results || []).filter(
        r => r.edge_node_id === siteKey ||
             r.edge_node_id === siteName ||
             r.edge_node_id.toUpperCase() === siteKey
      );
      console.log('[CORR-004] matched rows:', rows.length, rows);

      if (rows.length === 0) {
        console.warn('[CORR-004] no rows matched for', siteKey);
        setCorr004Site(null);
        return;
      }
      const indexed = {};
      rows.forEach(r => { indexed[r.correlator] = r; });
      console.log('[CORR-004] indexed:', indexed);
      setCorr004Site(indexed);
    } catch (e) {
      console.error('[CORR-004] fetch error:', e);
      setCorr004Site(null);
    } finally {
      setCorr004Loading(false);
    }
  }, []);

  // ── Topology rebuild (whenever site selection or data changes) ───────────

  const rebuildTopology = useCallback((sites, events, selSite, currentThreats, correlations) => {
    const result = buildTopology(sites, events, selSite, currentThreats, MOCK_DC_ASSETS);

    // Add cross-network correlation edges if correlations are active
    const extraEdges = [...result.edges];
    if (correlations.length > 0) {
      const siteNames = selSite
        ? [selSite]
        : Object.keys(sites).length > 0 ? Object.keys(sites) : ['DEMO'];

      siteNames.forEach(sname => {
        extraEdges.push({
          id: `corr-cross-${sname}`,
          source: `agent-ot-${sname}`,
          target: `agent-oob-${sname}`,
          animated: true,
          style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '6 3' },
          label: '🔗 CORR',
          labelStyle: { fill: '#ef4444', fontSize: 9, fontWeight: 'bold' },
          labelBgStyle: { fill: '#fff5f5', fillOpacity: 0.9 },
        });
      });
    }

    setFlowNodes(result.nodes);
    setFlowEdges(extraEdges);
    setStats(result.stats);
    setUseMock(result.useMock);
  }, [setFlowNodes, setFlowEdges]);

  // ── Main data fetch ──────────────────────────────────────────────────────

  const fetchData = useCallback(async (isManual = false) => {
    try {
      if (isManual) setIsRefreshing(true);
      else if (!isRefreshing) setLoading(prev => prev ? prev : false); // only show full loader on first load

      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);

      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];

      // Filter to DC agents only (edge_node_id starts with DC_)
      const dcAgents = edgeNodes.filter(n => n.edge_node_id?.toUpperCase().startsWith('DC_'));
      const sites = groupBySite(dcAgents);

      setEdgeNodesList(dcAgents);
      setDcSites(sites);
      setAllEvents(events);

      const newThreats = detectThreatsHeuristic(events);
      setThreats(newThreats);

      // Auto-select first site if none selected and sites exist
      const siteName = selectedSite || (Object.keys(sites).length > 0 ? Object.keys(sites)[0] : null);
      if (!selectedSite && siteName) setSelectedSite(siteName);

      // Fetch correlations and rebuild topology together
      let corrEvents = correlationEvents;
      try {
        const corrRaw = await eventsService.getEvents({ limit: 50 });
        corrEvents = (corrRaw || []).filter(e => e.event_type?.startsWith('CorrelationAlert_'));
        setCorrelationEvents(corrEvents);
      } catch {}

      rebuildTopology(sites, events, siteName, newThreats, corrEvents);

      if (!aiBriefing && !isManual) fetchBriefing();
    } catch (err) {
      console.error('DCSentry fetch failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [selectedSite, correlationEvents, aiBriefing, fetchBriefing, rebuildTopology, isRefreshing]);

  // ── Initial load + 30s polling ───────────────────────────────────────────

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Rebuild on site selection change ────────────────────────────────────

  useEffect(() => {
    if (Object.keys(dcSites).length > 0 || useMock) {
      rebuildTopology(dcSites, allEvents, selectedSite, threats, correlationEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite) fetchCorr004Site(selectedSite);
  }, [selectedSite, fetchCorr004Site]);

  const handleManualRefresh = () => fetchData(true);

  const handleApproveVPN = useCallback(async (action) => {
    try {
      return await executeNimbusAction('vpn_profile_change', {
        agent_id: action.agentId || 'Stratus5G_5728_Austin',
        current_profile: 'Standard',
        new_profile: 'Isolated-Secure',
        threat_source: action.device,
        target_devices: action.targetDevices,
        reason: action.reason,
      });
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-white relative overflow-hidden">
      {/* Dot-grid background */}
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)`,
        backgroundSize: '40px 40px',
      }} />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-white via-white to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Left: Title + Site Selector */}
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <span className="w-9 h-9 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
                  <span className="text-lg">🤖</span>
                </span>
                DC Sentry
              </h1>
              <p className="text-slate-500 text-xs mt-0.5">Data Center Security • OT + OOB Monitoring • Correlation Engine</p>
            </div>
            <DCSiteSelector
              sites={useMock ? MOCK_SITES_MAP : dcSites}
              selectedSite={selectedSite}
              onSelect={setSelectedSite}
            />
            {useMock && (
              <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
                <span className="text-amber-700 text-[10px] font-medium">Demo topology — deploy DC agents to populate live data</span>
              </div>
            )}
          </div>

          {/* Right: Stats */}
          <StatsBar
            stats={stats}
            threatsCount={threats.length}
            correlationCount={correlationEvents.length}
            onOpenPanel={() => { setIsAIPanelOpen(true); }}
            onRequestBriefing={handleRequestBriefing}
            isRefreshing={isRefreshing}
            onRefresh={handleManualRefresh}
          />
        </div>
      </div>

      {/* CORR-004 Site Intelligence Banner */}
      {selectedSite && (
        <SiteIntelBanner
          siteName={selectedSite}
          data={corr004Site}
          loading={corr004Loading}
        />
      )}

      {/* Correlation Banner */}
      <CorrelationBanner
        correlationEvents={correlationEvents}
        onViewDetails={() => { setIsAIPanelOpen(true); }}
      />

      {/* Shift Briefing Modal */}
      <ShiftBriefingModal
        isOpen={showBriefing && !loading}
        onClose={() => setShowBriefing(false)}
        aiBriefing={aiBriefing}
        stats={stats}
        threats={threats}
        isLoadingAI={isLoadingAI}
        onRequestExpanded={fetchExpandedBriefing}
        expandedBriefing={expandedBriefing}
        isLoadingExpanded={isLoadingExpanded}
      />

      {/* Full-page loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="text-center">
            <div className="w-14 h-14 border-4 border-cyan-200 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-600 text-base font-medium">DC Sentry Initializing...</div>
            <div className="text-slate-400 text-xs mt-1">Connecting to OT + OOB agents</div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <div className="text-red-600 text-base font-medium mb-2">Failed to connect to DC agents</div>
            <div className="text-red-400 text-sm">{error}</div>
            <button onClick={() => fetchData()} className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">Retry</button>
          </div>
        </div>
      )}

      {/* React Flow */}
      {!loading && !error && (
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.35 }}
          minZoom={0.15}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(0, 0, 0, 0.04)" />
          <Controls className="!bg-white !border-slate-200 !rounded-xl !shadow-lg" />

          {/* Legend */}
          <Panel position="bottom-right">
            <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-xl p-3 shadow-lg space-y-2 mr-2 mb-2 text-xs">
              <div className="font-semibold text-slate-600 uppercase tracking-wider text-[9px]">Network Planes</div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-orange-400" />
                <span className="text-slate-600">OT Network (Facilities)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-purple-500" />
                <span className="text-slate-600">OOB Network (Management)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 border-t-2 border-dashed border-red-500" />
                <span className="text-red-600 font-medium">Cross-Network Correlation</span>
              </div>
              <div className="border-t border-slate-100 pt-2 mt-1">
                <div className="font-semibold text-slate-600 uppercase tracking-wider text-[9px] mb-1">AI Status</div>
                {Object.values(AI_STATUS).map(s => (
                  <div key={s.text} className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px]">{s.icon}</span>
                    <span className="text-slate-500">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </ReactFlow>
      )}

      {/* Chat Panel */}
      {!loading && !error && (
        <div className="absolute bottom-4 left-4 z-30">
          <ChatPanel threats={threats} stats={stats} selectedSite={selectedSite} corr004Data={corr004Site} />
        </div>
      )}

      {/* AI Panel */}
      <AIPanel
        isOpen={isAIPanelOpen}
        onClose={() => setIsAIPanelOpen(false)}
        threats={threats}
        allEvents={allEvents}
        correlationEvents={correlationEvents}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        onApproveVPN={handleApproveVPN}
      />

      <style>{`
        @keyframes slide-in-left  { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left  { animation: slide-in-left  0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default DCSentry;
